import React, { useEffect, useRef, useState } from 'react';
import { LogoIcon } from 'svgs';
import { CreditCard, PaymentForm } from 'react-square-web-payments-sdk';
import type { TokenResult } from '@square/web-sdk';
import {
  MarketBanner,
  MarketButton,
  MarketField,
  MarketInputText,
} from '@market/react';
import type { ReceiptLookupPaymentRecord } from 'store/query/receiptLookup';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
// eslint-disable-next-line import/no-named-as-default
import ReCAPTCHA from 'react-google-recaptcha';
import * as Sentry from '@sentry/react';
import config from 'config/config';
import DateInput from './DateInput';
import { useSearchReceiptsMutation } from 'store/query/receiptLookup';
import { environment } from 'utils/environment';
import Es2Tracker from 'services/tracking/tracker';
import { searchReceipts as searchReceiptsEvent } from 'services/tracking/events/receiptLookupEvents';
import { searchReceipts as searchReceiptsViaBuyerportal } from 'services/buyerportal';
import { getCurrencyFromLocale } from 'utils/localeCurrency';
import { getMoneyFromFormattedString } from 'utils/currency';
import { AppState } from 'store';
import { selectBoolFlag } from 'store/featureSlice';
import { AnonymousBoolFlag } from 'routes/profile/models/Flags';
import SquareInformational from './SquareInformational';

// Allows a staging captcha token to be used in development, mirroring store/query/receiptLookup.
const DEV_CAPTCHA_TOKEN = 'receipts_staging_test_token';

type Props = {
  paymentRecords?: ReceiptLookupPaymentRecord[];
  setPaymentRecords: (records: ReceiptLookupPaymentRecord[]) => void;
};

const recaptchaPublicKey =
  environment().name === 'development'
    ? config.recaptcha.devKey
    : config.recaptcha.publicKey;

const isValidAmount = (value: string | undefined): value is string => {
  if (value === undefined || value.trim() === '') {
    return false;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0;
};

export { isValidAmount };

// Cloudflare bot mitigation rejects requests with a 403 and a non-JSON
// body, which RTK Query surfaces as PARSING_ERROR with the HTTP status on
// originalStatus instead of status.
const getHttpStatus = (
  error: FetchBaseQueryError | SerializedError
): number | undefined => {
  if ('status' in error) {
    if (typeof error.status === 'number') {
      return error.status;
    }
    if (error.status === 'PARSING_ERROR') {
      return error.originalStatus;
    }
  }
  return undefined;
};

type SearchError = 'blocked' | 'captcha' | 'generic';

const classifySearchError = (
  error: FetchBaseQueryError | SerializedError
): SearchError => {
  // The receipts backend rejects a failed reCAPTCHA verification with a
  // 400 and {"error": "invalid_recaptcha"}.
  if (
    getHttpStatus(error) === 400 &&
    'data' in error &&
    (error.data as { error?: string } | null | undefined)?.error ===
      'invalid_recaptcha'
  ) {
    return 'captcha';
  }
  // 403s never come from the backend; they are Cloudflare bot-mitigation
  // blocks, challenges, and rate limits on the search endpoint.
  if (getHttpStatus(error) === 403) {
    return 'blocked';
  }
  return 'generic';
};

const searchErrorMessageKeys: Record<SearchError, string> = {
  blocked: 'receiptLookup.error.blocked',
  captcha: 'receiptLookup.error.captcha',
  generic: 'receiptLookup.error.generic',
};

const ReceiptLookupForm: React.FC<Props> = ({
  setPaymentRecords,
  paymentRecords,
}) => {
  const { t } = useTranslation();
  const [date, setDate] = useState<Date | null>(new Date());
  const [amount, setAmount] = useState<string>();
  const [isAmountFieldValid, setIsAmountFieldValid] = useState(true);
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>();
  const [formKey, setFormKey] = useState<number>(0); // Form key will allow us to remount PaymentForm when a request fails
  const [searchError, setSearchError] = useState<SearchError | null>(null);

  const [search, { isLoading: isSearching }] = useSearchReceiptsMutation({});
  const [isSearchingViaBuyerportal, setIsSearchingViaBuyerportal] =
    useState<boolean>(false);
  const otpEnabled = useSelector((state: AppState) =>
    selectBoolFlag(state, AnonymousBoolFlag.enableReceiptLookupOtp)
  );

  // Have to use MutationObserver to set the title of the iframe because the PaymentForm component does not allow us to set the title
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const iframe = document.querySelector('iframe.sq-card-component');
      if (iframe && !iframe.title) {
        iframe.title = t('receiptLookup.wpsdk.iframeTitle');
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [t]);

  const handleVerify = (token: string | null) => {
    setRecaptchaToken(token);
  };

  // New path: search via buyerportal (protobuf RPC). The `rlv` proof cookie set during OTP
  // verification rides along automatically (same origin, credentials included). The amount filter
  // needs an explicit currency, which the buyer never enters — infer it from the locale.
  const searchViaBuyerportal = async (token: TokenResult) => {
    const searchDate = date || new Date();
    const locale =
      typeof navigator === 'undefined' ? undefined : navigator.language;

    let amountMinorUnits: number | undefined;
    let currencyCode: string | undefined;
    if (amount) {
      const currency = getCurrencyFromLocale(locale);
      const money = getMoneyFromFormattedString(amount, currency, locale);
      // getMoneyFromFormattedString multiplies by 100, which can yield floats like 28.999999 for
      // "0.29"; the request `amount` is an int64, so round to whole minor units before sending.
      amountMinorUnits = Math.round(money.amount);
      currencyCode = money.currencyCode;
    }

    try {
      setIsSearchingViaBuyerportal(true);
      const payments = await searchReceiptsViaBuyerportal({
        amount: amountMinorUnits,
        currencyCode,
        dateSubmit: searchDate.toISOString().split('T')[0],
        nonce: token.token ?? '',
        offset: -Math.round(searchDate.getTimezoneOffset() / 60),
        recaptchaToken:
          environment().name === 'development'
            ? DEV_CAPTCHA_TOKEN
            : recaptchaToken ?? '',
      });
      setPaymentRecords(payments);
      if (payments.length === 0) {
        setFormKey((prevKey) => prevKey + 1); // Force a new PaymentForm session on empty results
      }
      Es2Tracker.trackReceiptsCatalog(
        searchReceiptsEvent(payments.map((payment) => payment?.paymentToken))
      );
    } catch (error) {
      Sentry.captureException(error);
      setPaymentRecords([]);
      setFormKey((prevKey) => prevKey + 1); // Force a new PaymentForm session on error
    } finally {
      setIsSearchingViaBuyerportal(false);
    }
  };

  // Legacy path: direct RTK Query call to the receipts SearchService.
  const searchViaReceiptsService = async (token: TokenResult) => {
    const response = await search({
      amount: amount as string,
      date: date || new Date(),
      nonce: token.token ?? '',
      recaptchaToken: recaptchaToken ?? '',
    });
    if ('data' in response) {
      setPaymentRecords(response.data.payments ?? []);
      if (response.data.payments?.length === 0) {
        setFormKey((prevKey) => prevKey + 1); // Increment form key to force new session in PaymentForm request
      }
      Es2Tracker.trackReceiptsCatalog(
        searchReceiptsEvent(
          response.data.payments?.map((payment) => payment?.paymentToken) ?? []
        )
      );
    } else if ('error' in response) {
      const errorType = classifySearchError(response.error);
      // Captcha rejections and Cloudflare bot-mitigation blocks are expected
      // in normal operation, so only unexpected failures go to Sentry.
      if (errorType === 'generic') {
        Sentry.captureException(
          new Error(
            `Receipt lookup search failed (status: ${
              getHttpStatus(response.error) ??
              ('status' in response.error ? response.error.status : 'unknown')
            })`
          ),
          { extra: { searchError: response.error } }
        );
      }
      setSearchError(errorType);
      setPaymentRecords([]);
      setFormKey((prevKey) => prevKey + 1); // Increment form key to force new session in PaymentForm request
    }
  };

  const handleCardNonceReceived = async (token: TokenResult) => {
    // The reCAPTCHA token can expire between clicking search and card
    // tokenization completing; submitting without one always fails with 403.
    if (!recaptchaToken) {
      setSearchError('captcha');
      setFormKey((prevKey) => prevKey + 1);
      recaptchaRef.current?.reset();
      return;
    }
    setSearchError(null);
    if (otpEnabled) {
      await searchViaBuyerportal(token);
    } else {
      await searchViaReceiptsService(token);
    }
    setRecaptchaToken(null);
    recaptchaRef.current?.reset();
  };

  const isBusy = isSearching || isSearchingViaBuyerportal;
  const showEmptyBanner =
    !isBusy && !searchError && paymentRecords?.length === 0;

  return (
    <div className={'flex justify-center mt-2 p-6'}>
      <main className={'flex flex-col max-w-[440px]'}>
        <LogoIcon className={'text-[32px] mb-2'} />
        <h1 className="heading-30">{t('receiptLookup.title')}</h1>
        <p className="paragraph-30">{t('receiptLookup.description')}</p>
        {showEmptyBanner && (
          <div role="alert">
            <MarketBanner
              className="mt-2 mb-8"
              variant="critical"
              data-testid="empty-state-banner"
            >
              {t('receiptLookup.list.empty')}
            </MarketBanner>
          </div>
        )}
        {searchError && (
          <div role="alert">
            <MarketBanner
              className="mt-2 mb-8"
              variant="critical"
              data-testid="search-error-banner"
            >
              {t(searchErrorMessageKeys[searchError])}
            </MarketBanner>
          </div>
        )}

        <PaymentForm
          key={formKey}
          applicationId={config.squareDeveloper.applicationId}
          cardTokenizeResponseReceived={handleCardNonceReceived}
          locationId={config.squareDeveloper.locationId}
          overrides={{
            scriptSrc: config.squareDeveloper.webPaymentsSdkSrc,
          }}
        >
          <CreditCard
            render={(Button) => (
              <div>
                <DateInput
                  date={date}
                  onDateChanged={setDate}
                  isDateDisabled={(date) => date > new Date()}
                  className="mb-4"
                />
                <MarketField className="mb-4" invalid={!isAmountFieldValid}>
                  <span slot="error">{t('receiptLookup.amount.error')}</span>
                  <MarketInputText
                    data-testid="amount-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={amount}
                    onMarketInputValueChange={({ detail }) => {
                      setAmount(detail.value);
                      setIsAmountFieldValid(true);
                    }}
                    onBlur={() => setIsAmountFieldValid(isValidAmount(amount))}
                    id="receipt-lookup-amount-input"
                    required
                  >
                    <label htmlFor="receipt-lookup-amount-input">
                      {t('receiptLookup.label.amount')}*
                    </label>
                  </MarketInputText>
                </MarketField>
                <p className="paragraph-10 text-footerText">
                  {t('receiptLookup.confirmation')}
                </p>
                <div className="my-5">
                  <ReCAPTCHA
                    sitekey={recaptchaPublicKey}
                    onChange={handleVerify}
                    ref={recaptchaRef}
                  />
                </div>
                {recaptchaToken && !isBusy && date && isValidAmount(amount) ? (
                  <Button
                    isLoading={isBusy}
                    className="semibold-30 mt-3 rounded-buttonBorderRadius bg-emphasisFill hover:bg-emphasis20"
                  >
                    {t('receiptLookup.action.search')}
                  </Button>
                ) : (
                  <MarketButton
                    disabled
                    isLoading={isBusy}
                    className="semibold-30 mt-3 w-full"
                    rank="primary"
                    role="button"
                  >
                    {t('receiptLookup.action.search')}
                  </MarketButton>
                )}
              </div>
            )}
          />
        </PaymentForm>
        <SquareInformational />
      </main>
    </div>
  );
};

export default ReceiptLookupForm;
